import type { ApiImage } from './image'
import type { RecipeURN } from './identifiers'
import type { RecipePortion, RecipePricing, RecipeShelfLife, RecipeUUID } from './recipe'

export type ApiColor = {
  light: string
  dark: string
}

export type MenuRecipeDietaryTag = {
  type: 'plantBased' | 'vegetarian' | 'dairyFree' | 'glutenFree'
  text: string
  accessibleText: string
}

export type MenuRecipeGeneralTag = {
  type: 'generic' | 'soldOut' | 'invalidPreference'
  text: string
  color: ApiColor
  backgroundColor: ApiColor
}

export type MenuRecipeSubtitleTag = {
  type:
    | 'cookingTime'
    | 'energy'
    | 'protein'
    | 'fiveADay'
    | 'rating'
    | 'shelfLife'
    | 'spiceNone'
    | 'spiceMild'
    | 'spiceMedium'
    | 'spiceHot'
    | 'fat'
    | 'carbs'
    | 'fibre'
    | 'salt'
  text: string
  accessibleText: string
}

export type MenuRecipeBannerTag = {
  type: 'cookedByUs' | 'fiveADay' | 'noNasties'
  text: string
}

export type MenuRecipeBannerTagGroupColors = {
  color: ApiColor
  backgroundColor: ApiColor
}

export type MenuRecipeBannerTagGroup = {
  tags: MenuRecipeBannerTag[]
  colors?: MenuRecipeBannerTagGroupColors
}

export type MenuRecipeTagGroups = {
  dietary?: MenuRecipeDietaryTag[]
  general?: MenuRecipeGeneralTag[]
  compactCampaignBannerGeneral?: MenuRecipeGeneralTag[]
  subtitle?: MenuRecipeSubtitleTag[]
  banner?: MenuRecipeBannerTagGroup
  campaignBannerFooter?: MenuRecipeBannerTagGroup
}

export type StandardRecipeItemCustomisations = {
  type: 'ingredients'
  label: string
}

export type StandardRecipeItem = {
  type: 'standard-recipe'
  id: RecipeUUID
  urn?: RecipeURN
  legacyRecipeCoreId?: string
  optionIds?: RecipeUUID[]
  optionUrns?: RecipeURN[]
  image: ApiImage
  title: string
  isAvailable: boolean
  displayType: 'standard' | 'ready-to-heat'
  tagGroups: MenuRecipeTagGroups
  customisations?: StandardRecipeItemCustomisations
  pricing?: RecipePricing
  shelfLife?: RecipeShelfLife
  portions?: RecipePortion[]
}

export const NOTIFICATION_DISPLAY_TYPES = ['note', 'info', 'warning', 'error', 'success'] as const

export type NotificationDisplayType = typeof NOTIFICATION_DISPLAY_TYPES[number]

export type NotificationContentBlock = {
  type: 'notification'
  displayType: NotificationDisplayType
  title?: string
  messageHTML: string
}

export type ValueBannerContentBlock = {
  type: 'value-banner'
  descriptionHTML: string
  color: ApiColor
  backgroundColor: ApiColor
  // Added in NP-9156; absent until that has merged
  highlightColor?: ApiColor
  // Plain text, e.g. "Up to £10 off your box". Left out when the offer has no maximum discount
  supportingText?: string
}

export type CategoryBrandingContentBlock = {
  type: 'category-branding'
  backgroundImage: ApiImage
  backgroundColor: string
  textColor: string
  title: string
  description: string
}

export type WaveLinkHeaderContentBlock = {
  type: 'wave-link-header'
  title: string
  description: string
  headerImage: ApiImage
  categorySlug?: string
  categoryId?: string
  color: string
  backgroundColor: string
}

export type GradientInfoHeaderContentBlock = {
  type: 'gradient-info-header'
  title: string
  description: string
  headerImage: ApiImage
  color: string
  gradientColor: string
  image: ApiImage
  imageLocation: 'left' | 'right'
}

export type CampaignBannerCtaItem = {
  type: 'cta'
  title: string
  ctaText: string
  targetCategoryId: string
  targetCategorySlug: string
}

export type CampaignBannerHeaderContentBlock = {
  type: 'campaign-banner'
  backgroundImage: ApiImage
  backgroundColor: string
  textColor: string
  subtitle: string
  title: string
  description: string
  items: Array<StandardRecipeItem | CampaignBannerCtaItem>
}

const SUPPORTED_COMPACT_CAMPAIGN_GENERAL_TAG_TYPES = new Set<MenuRecipeGeneralTag['type']>([
  'generic',
  'soldOut',
  'invalidPreference',
])

export const getCompactCampaignBannerGeneralTags = (
  recipe: StandardRecipeItem,
): MenuRecipeGeneralTag[] =>
  (recipe.tagGroups.compactCampaignBannerGeneral ?? []).filter((tag) =>
    SUPPORTED_COMPACT_CAMPAIGN_GENERAL_TAG_TYPES.has(tag.type),
  )

export type CompactCampaignBannerHeaderContentBlock = {
  type: 'compact-campaign-banner'
  backgroundImage: ApiImage
  backgroundColor: string
  textColor: string
  title: string
  description: string
  ctaText: string
  targetCategoryId: string
  targetCategorySlug: string
  items: StandardRecipeItem[]
}

export type MerchandisingHeaderContentBlock =
  | CategoryBrandingContentBlock
  | WaveLinkHeaderContentBlock
  | GradientInfoHeaderContentBlock
  | CampaignBannerHeaderContentBlock
  | CompactCampaignBannerHeaderContentBlock
